import React from 'react';
import { Typography, Box, Stack, Paper, Chip } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

export const ScreenerAnimation: React.FC = () => (
  <Box
    sx={{
      height: '100%',
      width: '100%',
      position: 'relative',
      bgcolor: (theme) => theme.palette.mode === 'dark' ? '#000' : '#fff',
      borderRadius: 2,
      p: 3,
      color: 'white'
    }}
  >
    {/* Chart Section */}
    <Box sx={{ height: '60%', position: 'relative',  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', }}>
      <Typography variant="h6" gutterBottom>AAPL/USD</Typography>
      {/* Animated Chart Lines */}
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="200" viewBox="0 0 400 200">
        <defs>
          <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#3B82F6', stopOpacity: 0.1 }} />
            <stop offset="100%" style={{ stopColor: '#3B82F6', stopOpacity: 0.02 }} />
          </linearGradient>
        </defs>

        {/* Background grid */}
        <g stroke="#334155" strokeWidth="0.5" opacity="0.2">
          <line x1="0" y1="50" x2="400" y2="50" />
          <line x1="0" y1="100" x2="400" y2="100" />
          <line x1="0" y1="150" x2="400" y2="150" />
          <line x1="100" y1="0" x2="100" y2="200" />
          <line x1="200" y1="0" x2="200" y2="200" />
          <line x1="300" y1="0" x2="300" y2="200" />
        </g>

        {/* Main price line */}
        <path
          d="M0,100 C50,80 100,120 150,90 C200,60 250,110 300,70 C350,30 400,80 450,60"
          fill="none"
          stroke="#3B82F6"
          strokeWidth="2"
          strokeLinecap="round"
          style={{
            strokeDasharray: 400,
            strokeDashoffset: 400,
            animation: 'drawLine 2s forwards'
          }}
        />

        {/* Area under main line */}
        <path
          d="M0,100 C50,80 100,120 150,90 C200,60 250,110 300,70 C350,30 400,80 450,60 L450,200 L0,200 Z"
          fill="url(#chartGradient)"
          style={{
            opacity: 0,
            animation: 'fadeIn 1s forwards 0.5s'
          }}
        />

        {/* MACD Section */}
        <g transform="translate(0, 150)">
          <rect 
            x="0" 
            y="0" 
            width="400" 
            height="40" 
            fill="rgba(59, 130, 246, 0.1)"
          />
          
          {/* MACD Line */}
          <path
            d="M0,20 C50,10 100,30 150,15 C200,0 250,25 300,10 C350,5 400,15 450,10"
            fill="none"
            stroke="#EC4899"
            strokeWidth="1.5"
            strokeLinecap="round"
            style={{
              strokeDasharray: 400,
              strokeDashoffset: 400,
              animation: 'drawLine 2s forwards 1s'
            }}
          />
        </g>

        {/* Price Labels */}
        <text x="5" y="55" fill="#64748B" fontSize="12">50,000</text>
        <text x="5" y="105" fill="#64748B" fontSize="12">40,000</text>
        <text x="5" y="155" fill="#64748B" fontSize="12">30,000</text>

        {/* Volume Bars */}
        <g transform="translate(0, 170)">
          {[...Array(8)].map((_, i) => (
            <rect
              key={i}
              x={i * 50}
              y={0}
              width="30"
              height={10 + Math.random() * 20}
              fill="#3B82F6"
              opacity="0.3"
              style={{
                animation: 'growUp 1s forwards',
                animationDelay: `${i * 0.1}s`
              }}
            />
          ))}
        </g>
      </svg>
      
      {/* CSS Animations */}
      <Box
        sx={{
          '@keyframes drawLine': {
            to: {
              strokeDashoffset: 0
            }
          },
          '@keyframes fadeIn': {
            to: {
              opacity: 1
            }
          },
          '@keyframes growUp': {
            from: {
              height: 0
            }
          }
        }}
      />
    </Box>

    {/* Alert Configuration */}
    <Box
      sx={{
        mt: 2,
        opacity: 0,
        animation: 'fadeIn 2s forwards 3s',
        '@keyframes fadeIn': {
          to: { opacity: 1 }
        }
      }}
    >
      <Typography variant="h6" gutterBottom>Configure Alerts</Typography>
      <Stack spacing={2}>
        <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)' }}>
          <Typography variant="subtitle2">MACD Crossover Alert</Typography>
          <Stack direction="row" spacing={1} mt={1}>
            <Chip label="Webhook" size="small" />
            <Chip label="Email" size="small" />
            <Chip label="Trading" size="small" />
          </Stack>
        </Paper>
      </Stack>
    </Box>

    {/* Trading Integration */}
    <Box
      sx={{
        mt: 2,
        opacity: 0,
        animation: 'fadeIn 2s forwards 4s'
      }}
    >
      <Typography variant="subtitle1" gutterBottom>Connected Accounts</Typography>
      <Stack direction="row" spacing={2}>
        <Chip 
          icon={<CheckCircle />} 
          label="Kite" 
          color="success" 
          variant="outlined"
        />
        <Chip 
          icon={<CheckCircle />} 
          label="MetaTrader" 
          color="success" 
          variant="outlined"
        />
      </Stack>
    </Box>
  </Box>
);

export default ScreenerAnimation;